import { isAxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { type ProductDto, ProductsApi } from '@up2stay/up2promo-api-client';

import { productsQueryKeys } from '@/core/query-keys/products-query-keys';
import { useAuthStore } from '@/core/store/auth.store';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

/**
 * Loads the workspace profile’s BC product via stored id + GET by id (managed rows are not in search).
 */
export function useProduct(productId: string | null | undefined) {
  const user = useAuthStore((state) => state.user);
  const tokens = useAuthStore((state) => state.tokens);
  const currentWorkspace = useCurrentWorkspace();

  const workspaceId = currentWorkspace?.id;

  const queryKey = productsQueryKeys.detail(workspaceId, productId);

  const {
    data: product,
    isLoading,
    isPending,
    isFetching,
    isFetched,
    fetchStatus,
    status,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async (): Promise<ProductDto | null> => {
      if (!productId || !user || !tokens?.accessToken || !workspaceId) {
        return null;
      }

      try {
        const res = await ProductsApi.findOne({
          path: {
            workspaceId,
            id: productId,
          },
        });
        return res.data ?? null;
      } catch (err: unknown) {
        // The nested contract answers 404 productNotFound (it used to be an
        // empty 200); the detail page shows its "missing" state for both.
        if (isAxiosError(err) && err.response?.status === 404) return null;
        throw err;
      }
    },
    enabled:
      !!productId &&
      !!user &&
      !!tokens?.accessToken &&
      !!currentWorkspace &&
      !!workspaceId,
    staleTime: 30 * 1000,
  });

  const error =
    queryError && !product
      ? queryError instanceof Error
        ? queryError
        : new Error(String(queryError))
      : null;

  return {
    product: product ?? null,
    isLoading,
    isPending,
    isFetching,
    isFetched,
    fetchStatus,
    status,
    error,
    refetch: () => {
      void refetch();
    },
  };
}
